/**
 * Waiting Room Service — Wartezimmer-Management
 *
 * Tabellen: practice_waiting_zones, practice_waiting_sessions, practice_waiting_stats
 * Realtime: practice_waiting_sessions (INSERT/UPDATE/DELETE)
 */
import { supabase, isSupabaseConfigured, requireSupabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";

export class WaitingRoomServiceError extends Error {
    code: string;

    constructor(message: string, code = "WAITING_ROOM_ERROR") {
        super(message);
        this.name = "WaitingRoomServiceError";
        this.code = code;
    }
}

// ── Types ────────────────────────────────────────────────────

export type WaitingSessionStatus =
    | "checked_in"
    | "waiting"
    | "called"
    | "in_treatment"
    | "completed"
    | "no_show"
    | "cancelled";

export type WaitingUrgency = "normal" | "urgent" | "emergency";
export type CheckInMethod = "reception" | "patient_app" | "qr_code" | "self_service";

export interface WaitingZone {
    id: string;
    practice_id: string;
    name: string;
    description: string | null;
    capacity: number;
    room_number: string | null;
    display_order: number;
    is_default: boolean;
    is_active: boolean;
    created_at: string;
    current_count?: number;
}

export interface WaitingSession {
    id: string;
    practice_id: string;
    patient_id: string | null;
    appointment_id: string | null;
    zone_id: string | null;
    patient_name: string;
    patient_phone: string | null;
    status: WaitingSessionStatus;
    queue_number: number;
    queue_position: number;
    priority: number;
    check_in_time: string;
    called_at: string | null;
    treatment_started_at: string | null;
    treatment_ended_at: string | null;
    estimated_wait_minutes: number | null;
    actual_wait_minutes: number | null;
    assigned_doctor_id: string | null;
    assigned_room: string | null;
    check_in_method: CheckInMethod;
    check_in_by: string | null;
    visit_reason: string | null;
    urgency: WaitingUrgency;
    notes: string | null;
    internal_notes: string | null;
    metadata: Record<string, unknown>;
    created_at: string;
    updated_at: string;
    // Joins
    zone?: WaitingZone;
    patient?: {
        id: string;
        first_name: string;
        last_name: string;
        insurance_type: string;
        avatar_url: string | null;
    };
    doctor?: {
        id: string;
        title: string;
        first_name: string;
        last_name: string;
    };
    appointment?: {
        id: string;
        appointment_type: string;
        start_time: string;
    };
}

export interface WaitingStats {
    practice_id: string;
    stat_date: string;
    total_patients: number;
    completed_patients: number;
    no_show_count: number;
    cancelled_count: number;
    avg_wait_time: number | null;
    max_wait_time: number | null;
    min_wait_time: number | null;
    hourly_distribution: { hour: number; count: number }[];
    reception_checkins: number;
    app_checkins: number;
    qr_checkins: number;
}

export interface CheckInInput {
    practice_id: string;
    patient_id?: string;
    appointment_id?: string;
    patient_name: string;
    patient_phone?: string;
    zone_id?: string;
    visit_reason?: string;
    urgency?: WaitingUrgency;
    check_in_method?: CheckInMethod;
    check_in_by?: string;
    notes?: string;
}

export interface WaitingRoomKPIs {
    totalWaiting: number;
    avgWaitMinutes: number;
    inTreatment: number;
    checkedIn: number;
    overdue: number;
}

function extractErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message.trim()) {
        return error.message;
    }

    if (
        typeof error === "object" &&
        error !== null &&
        "message" in error &&
        typeof (error as { message?: unknown }).message === "string"
    ) {
        return ((error as { message: string }).message || "").trim();
    }

    return "";
}

function mapCheckInError(error: unknown): WaitingRoomServiceError {
    const message = extractErrorMessage(error);

    if (/duplicate key|already exists|bereits/i.test(message)) {
        return new WaitingRoomServiceError("Patient ist bereits eingecheckt.", "CHECK_IN_DUPLICATE");
    }

    if (/violates row-level security|permission denied|unauthorized/i.test(message)) {
        return new WaitingRoomServiceError(
            "Einchecken ist mit den aktuellen Berechtigungen nicht möglich.",
            "CHECK_IN_FORBIDDEN",
        );
    }

    if (message) {
        return new WaitingRoomServiceError(message, "CHECK_IN_FAILED");
    }

    return new WaitingRoomServiceError("Einchecken fehlgeschlagen.", "CHECK_IN_FAILED");
}

// ── SELECT helpers ──────────────────────────────────────────

const SESSION_SELECT = `
  *,
  zone:practice_waiting_zones(*),
  patient:patients(id, first_name, last_name, insurance_type, avatar_url),
  doctor:doctors(id, title, first_name, last_name),
  appointment:appointments(id, appointment_type, start_time)
`;

// ── Queries ─────────────────────────────────────────────────

export async function getWaitingZones(practiceId: string): Promise<WaitingZone[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        const { data, error } = await supabase
            .from("practice_waiting_zones")
            .select("*")
            .eq("practice_id", practiceId)
            .eq("is_active", true)
            .order("display_order", { ascending: true });
        if (error) throw error;
        return data || [];
    } catch (e) {
        logger.error("getWaitingZones", e);
        return [];
    }
}

export async function getActiveWaitingSessions(practiceId: string): Promise<WaitingSession[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        const today = new Date().toISOString().split("T")[0];
        const { data, error } = await supabase
            .from("practice_waiting_sessions")
            .select(SESSION_SELECT)
            .eq("practice_id", practiceId)
            .gte("check_in_time", `${today}T00:00:00`)
            .not("status", "in", '("completed","no_show","cancelled")')
            .order("priority", { ascending: false })
            .order("queue_position", { ascending: true });
        if (error) throw error;
        return data || [];
    } catch (e) {
        logger.error("getActiveWaitingSessions", e);
        return [];
    }
}

export async function getWaitingStats(
    practiceId: string,
    date?: string,
): Promise<WaitingStats | null> {
    if (!isSupabaseConfigured()) return null;
    try {
        const targetDate = date || new Date().toISOString().split("T")[0];
        const { data, error } = await supabase
            .from("practice_waiting_stats")
            .select("*")
            .eq("practice_id", practiceId)
            .eq("stat_date", targetDate)
            .single();
        if (error) {
            if (error.code === "PGRST116") return null;
            throw error;
        }
        return data;
    } catch (e) {
        logger.error("getWaitingStats", e);
        return null;
    }
}

export function computeKPIs(sessions: WaitingSession[]): WaitingRoomKPIs {
    const now = Date.now();
    const waiting = sessions.filter((s) => s.status === "checked_in" || s.status === "called");
    const inTreatment = sessions.filter((s) => s.status === "in_treatment");
    const checkedIn = sessions.filter((s) => s.status === "checked_in");

    const waitTimes = waiting.map((s) => (now - new Date(s.check_in_time).getTime()) / 60000);
    const avgWait =
        waitTimes.length > 0
            ? Math.round(waitTimes.reduce((a, b) => a + b, 0) / waitTimes.length)
            : 0;
    const overdue = waitTimes.filter((m) => m > 30).length;

    return {
        totalWaiting: waiting.length,
        avgWaitMinutes: avgWait,
        inTreatment: inTreatment.length,
        checkedIn: checkedIn.length,
        overdue,
    };
}

// ── Mutations ───────────────────────────────────────────────

export async function checkInPatient(input: CheckInInput): Promise<WaitingSession> {
    const sb = requireSupabase();
    try {
        let zoneId = input.zone_id;
        if (!zoneId) {
            // Get default zone
            const { data: zones } = await sb
                .from("practice_waiting_zones")
                .select("id")
                .eq("practice_id", input.practice_id)
                .eq("is_default", true)
                .limit(1);
            zoneId = zones?.[0]?.id;
        }
        const { data, error } = await sb
            .from("practice_waiting_sessions")
            .insert({
                ...input,
                zone_id: zoneId,
                status: "checked_in" as const,
                priority: input.urgency === "emergency" ? 2 : input.urgency === "urgent" ? 1 : 0,
            })
            .select(SESSION_SELECT)
            .single();
        if (error) throw error;

        // Strangler Fig: Keep legacy appointments table in sync
        if (input.appointment_id) {
            const { error: appointmentError } = await sb
                .from("appointments")
                .update({ status: "arrived" })
                .eq("id", input.appointment_id);

            if (appointmentError) {
                throw appointmentError;
            }
        }

        return data;
    } catch (e) {
        const serviceError = mapCheckInError(e);
        logger.error("checkInPatient failed", { error: serviceError.message, code: serviceError.code });
        throw serviceError;
    }
}

export async function callPatient(
    sessionId: string,
    doctorId?: string,
    room?: string,
): Promise<boolean> {
    const sb = requireSupabase();
    try {
        const { data, error } = await sb
            .from("practice_waiting_sessions")
            .update({
                status: "called" as const,
                assigned_doctor_id: doctorId,
                assigned_room: room,
            })
            .eq("id", sessionId)
            .select("appointment_id")
            .single();
        if (error) throw error;

        // Strangler Fig: Keep legacy in sync if there's an appointment attached
        if (data?.appointment_id) {
            await sb
                .from("appointments")
                .update({ status: "in_treatment" })
                .eq("id", data.appointment_id);
        }

        return true;
    } catch (e) {
        logger.error("callPatient", e);
        return false;
    }
}

export async function startTreatment(sessionId: string): Promise<boolean> {
    const sb = requireSupabase();
    try {
        const { data, error } = await sb
            .from("practice_waiting_sessions")
            .update({ status: "in_treatment" as const })
            .eq("id", sessionId)
            .select("appointment_id")
            .single();
        if (error) throw error;

        if (data?.appointment_id) {
            await sb
                .from("appointments")
                .update({ status: "in_treatment" })
                .eq("id", data.appointment_id);
        }
        return true;
    } catch (e) {
        logger.error("startTreatment", e);
        return false;
    }
}

export async function completeTreatment(sessionId: string, notes?: string): Promise<boolean> {
    const sb = requireSupabase();
    try {
        const { data, error } = await sb
            .from("practice_waiting_sessions")
            .update({ status: "completed" as const, notes })
            .eq("id", sessionId)
            .select("appointment_id")
            .single();
        if (error) throw error;

        if (data?.appointment_id) {
            await sb
                .from("appointments")
                .update({ status: "completed" })
                .eq("id", data.appointment_id);
        }
        return true;
    } catch (e) {
        logger.error("completeTreatment", e);
        return false;
    }
}

export async function markNoShow(sessionId: string): Promise<boolean> {
    const sb = requireSupabase();
    try {
        const { data, error } = await sb
            .from("practice_waiting_sessions")
            .update({ status: "no_show" as const })
            .eq("id", sessionId)
            .select("appointment_id")
            .single();
        if (error) throw error;

        if (data?.appointment_id) {
            await sb
                .from("appointments")
                .update({ status: "no_show" })
                .eq("id", data.appointment_id);
        }
        return true;
    } catch (e) {
        logger.error("markNoShow", e);
        return false;
    }
}

// ── Cancel / Zone / Priority ────────────────────────────────

export async function cancelWaitingSession(sessionId: string): Promise<boolean> {
    const sb = requireSupabase();
    try {
        const { data, error } = await sb
            .from("practice_waiting_sessions")
            .update({ status: "cancelled" as const })
            .eq("id", sessionId)
            .select("appointment_id")
            .single();
        if (error) throw error;

        if (data?.appointment_id) {
            await sb
                .from("appointments")
                .update({ status: "cancelled" })
                .eq("id", data.appointment_id);
        }
        return true;
    } catch (e) {
        logger.error("cancelWaitingSession", e);
        return false;
    }
}

export async function changeZone(sessionId: string, zoneId: string): Promise<boolean> {
    const sb = requireSupabase();
    try {
        const { error } = await sb
            .from("practice_waiting_sessions")
            .update({ zone_id: zoneId })
            .eq("id", sessionId);
        if (error) throw error;
        return true;
    } catch (e) {
        logger.error("changeZone", e);
        return false;
    }
}

export async function updatePriority(sessionId: string, priority: 0 | 1 | 2): Promise<boolean> {
    const sb = requireSupabase();
    try {
        const { error } = await sb
            .from("practice_waiting_sessions")
            .update({ priority })
            .eq("id", sessionId);
        if (error) throw error;
        return true;
    } catch (e) {
        logger.error("updatePriority", e);
        return false;
    }
}

export async function updateSessionNotes(sessionId: string, notes: string): Promise<boolean> {
    const sb = requireSupabase();
    try {
        const { error } = await sb
            .from("practice_waiting_sessions")
            .update({ notes })
            .eq("id", sessionId);
        if (error) throw error;
        return true;
    } catch (e) {
        logger.error("updateSessionNotes", e);
        return false;
    }
}

export async function assignRoom(sessionId: string, room: string): Promise<boolean> {
    const sb = requireSupabase();
    try {
        const { error } = await sb
            .from("practice_waiting_sessions")
            .update({ assigned_room: room })
            .eq("id", sessionId);
        if (error) throw error;
        return true;
    } catch (e) {
        logger.error("assignRoom", e);
        return false;
    }
}

// ── Helpers ─────────────────────────────────────────────────

export function formatWaitTime(minutes: number): string {
    if (minutes < 1) return "< 1 Min";
    if (minutes < 60) return `${minutes} Min`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hrs} Std ${mins} Min` : `${hrs} Std`;
}

export function getUrgencyColor(urgency: WaitingUrgency): string {
    switch (urgency) {
        case "emergency":
            return "text-red-600";
        case "urgent":
            return "text-amber-600";
        default:
            return "text-neutral-600";
    }
}

export function getCheckInMethodLabel(method: CheckInMethod): string {
    switch (method) {
        case "patient_app":
            return "App";
        case "qr_code":
            return "QR-Code";
        case "self_service":
            return "Self-Service";
        default:
            return "Empfang";
    }
}

// ── Realtime ────────────────────────────────────────────────

export function subscribeToWaitingRoom(practiceId: string, onUpdate: () => void): () => void {
    if (!isSupabaseConfigured()) return () => {};

    const channel = supabase
        .channel(`waiting_room_${practiceId}`)
        .on(
            "postgres_changes",
            {
                event: "*",
                schema: "public",
                table: "practice_waiting_sessions",
                filter: `practice_id=eq.${practiceId}`,
            },
            () => onUpdate(),
        )
        .subscribe();

    return () => {
        supabase.removeChannel(channel);
    };
}
